import OpenAI from 'openai';
import { benchmarkInstrumentation, timeOperation } from '../utils/benchmarkInstrumentation';
import { getSdkOpenAiBaseUrl } from '../utils/openAiBaseUrl';
class OpenAiWrapper {
    // Subscribe to API key changes
    onApiKeyChange(listener) {
        this.apiKeyListeners.push(listener);
    }
    // Unsubscribe from API key changes
    offApiKeyChange(listener) {
        this.apiKeyListeners = this.apiKeyListeners.filter((l) => l !== listener);
    }
    onConfigChange(listener) {
        this.configListeners.push(listener);
    }
    offConfigChange(listener) {
        this.configListeners = this.configListeners.filter((l) => l !== listener);
    }
    // Notify listeners
    notifyApiKeyChange(key) {
        for (const listener of this.apiKeyListeners) {
            try {
                listener(key);
            }
            catch { }
        }
    }
    notifyConfigChange(config) {
        for (const listener of this.configListeners) {
            try {
                listener(config);
            }
            catch (e) {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[OpenAiWrapper] configChange listener error:', e);
                }
            }
        }
    }
    /**
     * Conversation helpers (system message + history)
     */
    setSystemMessage(message) {
        this.systemMessage = message;
    }
    addMessageToHistory(role, content) {
        this.conversationHistory.push({
            role,
            content,
            timestamp: new Date().toISOString(),
        });
    }
    getConversationMessages() {
        // Always include system message first if present
        const messages = [];
        if (this.systemMessage && this.systemMessage.trim()) {
            messages.push({
                role: 'system',
                content: this.systemMessage,
            });
        }
        // Add all conversation history (excluding any system messages already in history)
        const historyWithoutSystem = this.conversationHistory.filter(msg => msg.role !== 'system');
        messages.push(...historyWithoutSystem);
        return messages;
    }
    /**
     * Load conversation history (optionally from backend via railsApiWrapper)
     * railsApiWrapper may provide getChatHistory(threadId, options)
     */
    async loadConversationHistory(threadId, railsApiWrapper, historyOptions) {
        try {
            this.conversationHistory = [];
            // Always include current system message first (if provided)
            if (this.systemMessage && this.systemMessage.trim()) {
                this.conversationHistory.push({
                    role: 'system',
                    content: this.systemMessage,
                });
            }
            if (railsApiWrapper && typeof railsApiWrapper.getChatHistory === 'function') {
                const dbHistory = await railsApiWrapper.getChatHistory(threadId, historyOptions);
                if (dbHistory && Array.isArray(dbHistory)) {
                    for (const chat of dbHistory) {
                        if (chat.user_question) {
                            this.conversationHistory.push({
                                role: 'user',
                                content: chat.user_question,
                                timestamp: chat.created_at,
                            });
                        }
                        if (chat.chat_answer) {
                            this.conversationHistory.push({
                                role: 'assistant',
                                content: chat.chat_answer,
                                timestamp: chat.created_at,
                            });
                        }
                    }
                }
            }
        }
        catch {
            // Fallback to just system message
            this.conversationHistory = [];
            if (this.systemMessage && this.systemMessage.trim()) {
                this.conversationHistory.push({ role: 'system', content: this.systemMessage });
            }
        }
    }
    /**
     * Stream a response using OpenAI Responses API
     *
     * This is an alternative to streamChatCompletion() that uses the newer Responses API.
     * The Responses API manages conversation context on the server side using response IDs.
     *
     * @param input The user input text
     * @param promptId Hosted prompt id, or null/blank to send top-level `instructions` instead of `prompt`
     * @param options Optional model, temperature, maxTokens, and instructions
     * @param options.fallbackInput Replaces `input` whenever no previous_response_id is sent, including the retry after an invalid-ID 400
     * @param options.onChainReset Called right before the invalid-ID fallback retry, so the caller can clear its own chain state (e.g. before persisting chat metadata) to match the fresh chain the retry starts
     * @param previousResponseId Optional previous response ID (or resolver) for conversation continuity
     * @returns Promise with AsyncIterable stream (consumer should extract response.id from chunks)
     */
    async streamResponse(input, promptId, options = {}, previousResponseId, abortSignal) {
        const buildParams = (includePreviousResponseId = true) => {
            const resolvedModel = typeof options.model === 'function' ? options.model() : options.model;
            const resolvedPromptId = typeof promptId === 'string' && promptId.trim() !== ''
                ? promptId.trim()
                : null;
            const resolvedPreviousResponseId = includePreviousResponseId
                ? typeof previousResponseId === 'function'
                    ? previousResponseId()
                    : previousResponseId
                : undefined;
            const params = {
                model: resolvedModel || 'gpt-4.1-mini',
                input: resolvedPreviousResponseId
                    ? input
                    : (options.fallbackInput ?? input),
                stream: true,
                background: false,
                tool_choice: options.tools ? (options.tool_choice ?? 'auto') : 'none',
            };
            if (resolvedPromptId) {
                params.prompt = {
                    id: resolvedPromptId,
                };
            }
            else {
                params.instructions = options.instructions ?? '';
            }
            if (options.tools) {
                params.tools = options.tools;
            }
            if (options.temperature !== undefined) {
                params.temperature = options.temperature;
            }
            if (options.maxTokens !== undefined) {
                params.max_output_tokens = options.maxTokens;
            }
            if (options.reasoning) {
                params.reasoning = options.reasoning;
            }
            if (resolvedPreviousResponseId) {
                params.previous_response_id = resolvedPreviousResponseId;
            }
            return params;
        };
        // Add user message to history immediately
        // For multimodal content, convert to a simple string representation for history
        const historyContent = typeof input === 'string' ? input : '[Multimodal content: text + images]';
        this.addMessageToHistory('user', historyContent);
        const requestOptions = abortSignal ? { signal: abortSignal } : undefined;
        const makeOperation = (includePreviousResponseId) => async (client) => client.responses.create(buildParams(includePreviousResponseId), requestOptions);
        try {
            return await this.executeWithRetry(makeOperation(true), 5, 1000, 'responses_api_stream');
        }
        catch (error) {
            if (this.isPreviousResponseIdError(error) && previousResponseId) {
                // Tell the caller the chain is being reset *before* the retry fires, so it can
                // drop its own stale previous-response-id state (e.g. for chat metadata) instead
                // of persisting continuity that never actually happened on this request.
                options.onChainReset?.();
                return await this.executeWithRetry(makeOperation(false), 1, 0, 'responses_api_stream');
            }
            throw error;
        }
    }
    async submitToolCallResult(callIdOrOutputs, output, previousResponseId, promptId, options = {}, abortSignal) {
        const functionCallOutputs = Array.isArray(callIdOrOutputs)
            ? callIdOrOutputs.map(({ callId, output: o }) => ({
                type: 'function_call_output',
                call_id: callId,
                output: o,
            }))
            : [{ type: 'function_call_output', call_id: callIdOrOutputs, output: output ?? '' }];
        const inputItems = [
            ...functionCallOutputs,
            ...(options.additionalInputItems ?? []),
        ];
        const resolvedPromptId = typeof promptId === 'string' && promptId.trim() !== ''
            ? promptId.trim()
            : null;
        const params = {
            model: options.model || 'gpt-4.1-mini',
            input: inputItems,
            stream: true,
            background: false,
            tool_choice: options.tools ? (options.tool_choice ?? 'auto') : 'none',
            previous_response_id: previousResponseId,
        };
        if (options.tools) {
            params.tools = options.tools;
        }
        if (typeof options.temperature === 'number') {
            params.temperature = options.temperature;
        }
        if (options.reasoning) {
            params.reasoning = options.reasoning;
        }
        if (resolvedPromptId) {
            params.prompt = {
                id: resolvedPromptId,
            };
        }
        else {
            params.instructions = options.instructions ?? '';
        }
        const requestOptions = abortSignal ? { signal: abortSignal } : undefined;
        return await this.executeWithRetry(async (client) => {
            return await client.responses.create(params, requestOptions);
        }, 5, 1000, 'responses_api_tool_result');
    }
    setTTSInstructions(instructions) {
        this.ttsInstructions = instructions;
        if (typeof window !== 'undefined') {
            localStorage.setItem(this.wrapperConfig.ttsInstructionsKey || 'ttsInstructions', instructions);
        }
    }
    getTTSInstructions() {
        return this.ttsInstructions;
    }
    /**
     * Applies TTS instructions from a runtime source (e.g. the sofabuddy bootstrap /
     * DB-backed `tts_instructions` prompt) WITHOUT persisting to localStorage, so that
     * admin edits to the prompt are always picked up fresh on the next load.
     *
     * An explicit user/dev override stored in localStorage (set via TestControls using
     * `setTTSInstructions`) always wins and is never overwritten here. An empty value is
     * ignored so we never blank out existing instructions.
     */
    applyBootstrapTTSInstructions(instructions) {
        if (!instructions || instructions.trim().length === 0) {
            return;
        }
        const storageKey = this.wrapperConfig.ttsInstructionsKey || 'ttsInstructions';
        const storedOverride = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
        if (storedOverride && storedOverride.trim().length > 0) {
            return;
        }
        this.ttsInstructions = instructions;
    }
    // Helper to base64-encode Unicode strings safely (modern, no deprecated unescape)
    static base64EncodeUnicode(str) {
        const utf8Bytes = new TextEncoder().encode(str);
        let binary = '';
        utf8Bytes.forEach((b) => (binary += String.fromCharCode(b)));
        return btoa(binary);
    }
    // Remove static getTTSCacheKey from public API
    // Add a private cache key generator
    _getTTSCacheKey(text, instructions, voice, language) {
        const keyString = [text, instructions, voice, language].join('|');
        return OpenAiWrapper.base64EncodeUnicode(keyString);
    }
    /**
     * Get or generate streaming TTS URL for realtime playback
     * This method provides true streaming audio that starts playing immediately as data arrives
     * @param text The text to synthesize
     * @param voice Optional voice (e.g., 'alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer')
     * @param language Optional language code (e.g., 'de', 'en')
     * @returns Promise<string> streaming audio URL that starts playing immediately
     */
    /**
     * Stream TTS audio and cache the resulting audio blob for reliable replays.
     * - First call: returns a MediaSource-backed URL for immediate playback, while buffering chunks
     * - After completion: stores a Blob in an internal cache for future replays
     * - Subsequent calls: returns a fresh blob: URL created from cached blob data (no re-streaming)
     */
    async getStreamingTTSUrlWithCaching(text, voice, language) {
        if (!text || text.trim().length === 0) {
            throw new Error('No text provided for speech synthesis.');
        }
        // MediaSource support handling is delegated to the TtsAudioPlayer
        // Provide a dynamic fetch function that builds the payload from the current call's text/options
        const fetchTts = async (inputText, opts) => {
            const payload = {
                model: opts?.model || 'gpt-4o-mini-tts',
                input: inputText,
                voice: opts?.voice || 'shimmer',
                response_format: 'mp3',
            };
            const instructions = opts?.instructions ?? this.ttsInstructions;
            if (instructions?.trim()) {
                payload.instructions = instructions;
            }
            if (opts?.language)
                payload.language = opts.language;
            const url = this.getApiUrl('/audio/speech');
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${this.openAiKey}`,
                    'Content-Type': 'application/json',
                    'Accept': opts?.mimeType ?? 'audio/mpeg',
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Streaming TTS request failed: ${response.status} ${errorText}`);
            }
            if (response.body)
                return response.body;
            if (typeof response.arrayBuffer === 'function')
                return await response.arrayBuffer();
            if (typeof response.blob === 'function') {
                const blob = await response.blob();
                if (typeof blob.arrayBuffer === 'function')
                    return await blob.arrayBuffer();
                if (typeof blob.text === 'function') {
                    const text = await blob.text();
                    return new TextEncoder().encode(text).buffer;
                }
            }
            return new Uint8Array(0).buffer;
        };
        // Resolve player via getter (lazy-init with provided fetchTts)
        const player = await this.getPlayer(fetchTts);
        return player.getStreamingUrlWithBlobCache(text, {
            voice,
            language,
            mimeType: 'audio/mpeg',
            instructions: this.ttsInstructions,
            model: 'gpt-4o-mini-tts'
        });
    }
    /**
     * Clear cached TTS (delegated to player blob cache)
     */
    async clearTTSCacheForText(text, voice, language) {
        const player = await this.getPlayer();
        if (player?.clearBlobCacheByText) {
            player.clearBlobCacheByText(text, {
                voice,
                language,
                mimeType: 'audio/mpeg',
                instructions: this.ttsInstructions,
                model: 'gpt-4o-mini-tts'
            });
        }
    }
    async getPlayer(fetchTts) {
        if (!this['__player']) {
            const { TtsAudioPlayer } = await import('../audio/TtsAudioPlayer');
            // For calls like clearTTSCacheForText() we don't need fetchTts; use a noop
            const noopFetch = async () => new Uint8Array(0).buffer;
            this['__player'] = new TtsAudioPlayer({ fetchTts: fetchTts ?? noopFetch });
        }
        return this['__player'];
    }
    // Streaming implementation is handled within this wrapper (not delegated)
    // Public constructor for instance-based pattern
    constructor(config, wrapperConfig) {
        // --- Conversation state (generic, reusable) ---
        this.conversationHistory = [];
        this.systemMessage = '';
        // Event listeners for API key changes
        this.apiKeyListeners = [];
        this.configListeners = [];
        // Add TTS instructions property
        this.ttsInstructions = '';
        this.wrapperConfig = wrapperConfig;
        this.openAiKey = config.apiKey;
        this.baseURL = getSdkOpenAiBaseUrl(config.baseURL);
        this.app = config.app || wrapperConfig.app || 'sofatutor';
        this.feature = config.feature || wrapperConfig.feature;
        this.keyFetchPromiseRef = null;
        this.openAiClient = null;
        this.debug = true;
        this.initializeCustomerInfo();
        // Initialize ttsInstructions from localStorage or default
        const storedInstructions = typeof window !== 'undefined'
            ? localStorage.getItem(wrapperConfig.ttsInstructionsKey || 'ttsInstructions')
            : null;
        this.ttsInstructions =
            storedInstructions || wrapperConfig.defaultTtsInstructions || '';
    }
    applyConfig(config) {
        const normalizedBaseUrl = getSdkOpenAiBaseUrl(config.baseURL);
        const nextFeature = config.feature ?? this.feature;
        const shouldResetClient = this.openAiKey !== config.apiKey ||
            this.baseURL !== normalizedBaseUrl ||
            this.app !== (config.app || this.wrapperConfig.app || 'sofatutor') ||
            this.feature !== nextFeature;
        this.openAiKey = config.apiKey;
        this.baseURL = normalizedBaseUrl;
        this.app = config.app || this.wrapperConfig.app || 'sofatutor';
        this.feature = nextFeature;
        if (shouldResetClient) {
            this.openAiClient = null;
        }
    }
    hasApiKey() {
        return Boolean(this.openAiKey && this.openAiKey.length > 0);
    }
    initializeCustomerInfo() {
        // Get CSRF token from meta tag
        if (typeof document !== 'undefined') {
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            this.csrfToken = csrfMeta ? csrfMeta.content : '';
        }
    }
    /**
     * Make a proxy request to the Rails backend
     * @param endpoint API endpoint path
     * @param options Optional fetch options
     * @returns Promise with JSON response
     */
    async proxyRequest(endpoint, options = {}) {
        const url = `${this.wrapperConfig.proxyEndpoint || '/api/ai_proxy'}${endpoint}`;
        const fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': this.csrfToken,
            },
            ...options,
        };
        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    async getOpenAiClient() {
        // Only create a new client if we don't already have one
        if (!this.openAiClient) {
            this.openAiClient = new OpenAI({
                apiKey: this.openAiKey,
                dangerouslyAllowBrowser: true,
                baseURL: this.baseURL,
            });
        }
        return this.openAiClient;
    }
    async getNewOpenAiKey() {
        // If there's already a key fetch in progress, wait for it
        if (this.keyFetchPromiseRef) {
            await this.keyFetchPromiseRef;
            return;
        }
        if (!this.wrapperConfig.railsApiWrapper ||
            typeof this.wrapperConfig.railsApiWrapper.generateOpenAiKey !== 'function') {
            throw new Error('[OpenAiWrapper] railsApiWrapper with generateOpenAiKey() must be provided in wrapperConfig');
        }
        // Create the fetch promise and store it
        this.keyFetchPromiseRef = (async () => {
            try {
                const data = await this.wrapperConfig.railsApiWrapper.generateOpenAiKey(this.feature);
                if (data?.token) {
                    const nextConfig = {
                        apiKey: data.token,
                        baseURL: data.baseURL || this.baseURL,
                        app: data.app || this.app,
                        feature: data.feature || this.feature,
                        provider: data.provider,
                        model: data.model,
                        promptId: data.promptId,
                    };
                    this.applyConfig({
                        apiKey: nextConfig.apiKey || '',
                        baseURL: nextConfig.baseURL || this.baseURL,
                        app: nextConfig.app,
                        feature: nextConfig.feature,
                        model: nextConfig.model,
                    });
                    this.notifyApiKeyChange(this.openAiKey);
                    this.notifyConfigChange({
                        ...nextConfig,
                        apiKey: this.openAiKey,
                        baseURL: this.baseURL,
                        app: this.app,
                        feature: this.feature,
                    });
                }
                else {
                    throw new Error('[OpenAiWrapper] Failed to get a valid OpenAI key from backend');
                }
            }
            catch (error) {
                throw error;
            }
            finally {
                // Clear the promise reference when done
                this.keyFetchPromiseRef = null;
            }
        })();
        await this.keyFetchPromiseRef;
    }
    /**
     * Ensure we have a valid OpenAI key, fetching one only if not already cached.
     * Unlike getNewOpenAiKey() which always fetches, this method is a no-op when
     * a key is already available.
     */
    async ensureOpenAiKey() {
        if (this.openAiKey && this.openAiKey.length > 0) {
            return;
        }
        await this.getNewOpenAiKey();
    }
    async ensureApiKey() {
        try {
            await this.ensureOpenAiKey();
            return this.hasApiKey();
        }
        catch {
            return false;
        }
    }
    isAuthOrConnectionError(error) {
        return ['Authentication', 'APIConnection', '401'].some((errorType) => error.message.includes(errorType) || error.name.includes(errorType));
    }
    extractHttpStatus(error) {
        if (typeof error?.status === 'number') {
            return error.status;
        }
        if (typeof error?.response?.status === 'number') {
            return error.response.status;
        }
        const message = typeof error?.message === 'string' ? error.message : '';
        const statusMatch = message.match(/\b(?:status:?|HTTP error|failed:)\s*(\d{3})\b/i);
        if (statusMatch) {
            return Number(statusMatch[1]);
        }
        return undefined;
    }
    isNonRetryableError(error) {
        const status = this.extractHttpStatus(error);
        if (status === undefined) {
            return false;
        }
        return status >= 400 &&
            status < 500 &&
            ![401, 408, 409, 429].includes(status);
    }
    isPreviousResponseIdError(error) {
        const message = typeof error?.message === 'string' ? error.message : '';
        return (this.extractHttpStatus(error) === 400 &&
            message.toLowerCase().includes('previous_response_id'));
    }
    throwOnHttpError(result) {
        if (result &&
            typeof result === 'object' &&
            'status' in result &&
            typeof result.status === 'number') {
            const status = result.status;
            if (status === 401 || status >= 500) {
                const statusText = result.statusText || '';
                throw new Error(`HTTP error ${status}: ${statusText}`);
            }
        }
    }
    async executeWithRetry(operation, maxRetries = 5, initialDelay = 1000, operationName) {
        let retries = 0;
        let lastError = null;
        if (!this.openAiKey || this.openAiKey.length === 0) {
            await this.getNewOpenAiKey();
        }
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const executeOperation = async () => {
            while (retries < maxRetries) {
                try {
                    const client = await this.getOpenAiClient();
                    const result = await operation(client);
                    this.throwOnHttpError(result);
                    return result;
                }
                catch (error) {
                    if (error?.name === 'AbortError') {
                        throw error;
                    }
                    lastError = error;
                    if (this.isNonRetryableError(error)) {
                        throw error;
                    }
                    if (this.isAuthOrConnectionError(error)) {
                        await this.getNewOpenAiKey();
                    }
                    else if (retries === maxRetries - 1) {
                        throw error;
                    }
                    retries++;
                    const delayMs = initialDelay * Math.pow(2, retries - 1);
                    await delay(delayMs);
                }
            }
            // One last self-heal attempt after auth/connection retry exhaustion.
            // This helps long-lived app sessions (e.g. mobile apps) recover without full page reload.
            if (lastError && this.isAuthOrConnectionError(lastError)) {
                await this.getNewOpenAiKey();
                const client = await this.getOpenAiClient();
                const result = await operation(client);
                this.throwOnHttpError(result);
                return result;
            }
            throw new Error('Max retries reached');
        };
        // Use instrumentation if operation name provided
        if (operationName && benchmarkInstrumentation.isInstrumentationEnabled()) {
            return timeOperation(operationName, executeOperation, { maxRetries, initialDelay });
        }
        else {
            return executeOperation();
        }
    }
    /**
     * Helper to safely join the baseURL and an endpoint path (no double slashes, no double /v1)
     * @param path Endpoint path, e.g. 'audio/transcriptions'
     * @returns Full URL string
     */
    getApiUrl(path) {
        return `${this.baseURL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    }
    /**
     * Transcribe audio using the `gpt-4o-mini-transcribe` OpenAI transcription model via proxy.
     * @param audioBlob Audio blob to transcribe
     * @returns Promise<string> transcript
     */
    async transcribeAudioWithModel(audioBlob) {
        if (!audioBlob || audioBlob.size === 0) {
            throw new Error('Empty audio recording. Please try speaking again.');
        }
        // Make sure the blob type is correct
        const processedBlob = audioBlob;
        if (audioBlob.type !== 'audio/webm' &&
            audioBlob.type !== 'audio/mpeg' &&
            audioBlob.type !== 'audio/mp4') {
            // We keep the blob but explicitly set the filename with extension
        }
        return this.executeWithRetry(async () => {
            // Add a timestamp to avoid caching
            const timestamp = Date.now();
            const url = this.getApiUrl('audio/transcriptions') + `?_nocache=${timestamp}`;
            const formData = new FormData();
            // Important: Make sure the filename has an extension that matches the MIME type
            // Add a timestamp to the filename to ensure it's unique
            let extension = 'mp3'; // default
            if (audioBlob.type.includes('webm')) {
                extension = 'webm';
            }
            else if (audioBlob.type.includes('m4a') || audioBlob.type.includes('mp4')) {
                extension = 'm4a';
            }
            else if (audioBlob.type.includes('wav')) {
                extension = 'wav';
            }
            else if (audioBlob.type.includes('flac')) {
                extension = 'flac';
            }
            else if (audioBlob.type.includes('ogg')) {
                extension = 'ogg';
            }
            const filename = `recording_${timestamp}.${extension}`;
            formData.append('file', processedBlob, filename);
            formData.append('model', 'gpt-4o-mini-transcribe');
            formData.append('language', 'de');
            formData.append('response_format', 'json');
            // Add a cache-buster parameter
            formData.append('timestamp', timestamp.toString());
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${this.openAiKey}`,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        Pragma: 'no-cache',
                        Expires: '0',
                    },
                    body: formData,
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Audio transcription failed: ${response.status} ${errorText}`);
                }
                const data = await response.json();
                if (typeof data.text === 'string' && data.text.trim()) {
                    return data.text;
                }
                else {
                    throw new Error('No transcription received from audio transcription API');
                }
            }
            catch (error) {
                throw error;
            }
        });
    }
    /**
     * Synthesize speech using OpenAI TTS via proxy
     * @param text The text to synthesize
     * @param voice Optional voice (e.g., 'alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer')
     * @param language Optional language code (e.g., 'de', 'en')
     * @returns Promise<Blob> audio blob
     */
    async synthesizeSpeechWithTTS(text, voice, language) {
        if (!text || text.trim().length === 0) {
            throw new Error('No text provided for speech synthesis.');
        }
        return this.executeWithRetry(async () => {
            // Create JSON payload according to OpenAI API specification
            const payload = {
                model: 'gpt-4o-mini-tts',
                input: text,
                voice: voice || 'shimmer',
                response_format: 'mp3',
            };
            if (this.ttsInstructions?.trim()) {
                payload.instructions = this.ttsInstructions;
            }
            if (language) {
                payload.language = language;
            }
            try {
                const url = this.getApiUrl('/audio/speech');
                // Use JSON approach instead of FormData for the TTS API
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${this.openAiKey}`,
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        Pragma: 'no-cache',
                        Expires: '0',
                    },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`TTS synthesis failed: ${response.status} ${errorText}`);
                }
                const audioBlob = await response.blob();
                if (!audioBlob || audioBlob.size === 0) {
                    throw new Error('No audio received from TTS API');
                }
                return audioBlob;
            }
            catch (error) {
                throw error;
            }
        });
    }
    /**
     * Send a PDF file as base64 to the OpenAI Vision API
     * @param file PDF file
     * @param prompt Prompt for the Vision API
     * @param model Optional model (default: gpt-4.1-mini)
     * @returns Promise<any> API response
     */
    async sendPdfToVisionApi(file, prompt, model = 'gpt-4.1-mini') {
        const maxPdfFileSize = this.wrapperConfig.maxPdfFileSize || 4 * 1024 * 1024;
        // Check file type
        if (file.type !== 'application/pdf') {
            throw new Error('Only PDF files are supported for Vision API PDF upload.');
        }
        // Check file size (max 4MB PDF for Vision API)
        if (file.size > maxPdfFileSize) {
            throw new Error(`PDF too large: ${Math.round(file.size / 1024 / 1024)}MB. The limit is ${maxPdfFileSize / (1024 * 1024)}MB for Vision API.`);
        }
        // Read file as base64
        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Remove the data:...;base64, prefix if present
                const result = reader.result;
                const base64Data = result.split(',')[1] || result;
                resolve(base64Data);
            };
            reader.onerror = () => {
                reject(new Error('Failed to read PDF file'));
            };
            reader.readAsDataURL(file);
        });
        // Build Chat Completions request using base64-embedded PDF content
        const body = {
            model,
            messages: [
                {
                    role: 'user',
                    content: [
                        { type: 'text', text: prompt },
                        {
                            type: 'file',
                            file: {
                                filename: file.name,
                                file_data: `data:application/pdf;base64,${base64}`,
                            },
                        },
                    ],
                },
            ],
            max_tokens: 4096,
        };
        return this.executeWithRetry(async () => {
            const url = `${this.baseURL.replace(/\/$/, '')}/chat/completions`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${this.openAiKey}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Vision API PDF upload failed: ${response.status} ${errorText}`);
            }
            return response.json();
        });
    }
    /**
     * Uploads a file (image or PDF) to OpenAI and returns the file_id
     * Uses purpose=vision for images, purpose=assistants for PDFs
     */
    async uploadFileForAssistant(file) {
        const validFileTypes = this.wrapperConfig.validFileTypes || [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'application/pdf',
        ];
        const maxImageFileSize = this.wrapperConfig.maxImageFileSize || 5 * 1024 * 1024;
        // Check for valid file format for the OpenAI API
        if (!validFileTypes.includes(file.type)) {
            throw new Error(`Unsupported file format: ${file.type}. Only JPEG, PNG, GIF, WEBP and PDF are supported.`);
        }
        // Check for maximum file size
        if (file.size > maxImageFileSize) {
            throw new Error(`File too large: ${Math.round(file.size / 1024 / 1024)}MB. The limit is ${maxImageFileSize / (1024 * 1024)}MB.`);
        }
        // --- Normalize file extension to lowercase for images ---
        let fileToUpload = file;
        if (file.type.startsWith('image/')) {
            const nameParts = file.name.split('.');
            if (nameParts.length > 1) {
                const ext = nameParts.pop();
                const lowerExt = ext ? ext.toLowerCase() : '';
                const newName = `${nameParts.join('.')}.${lowerExt}`;
                if (newName !== file.name) {
                    fileToUpload = new File([file], newName, { type: file.type });
                }
            }
        }
        // ---
        return this.executeWithRetry(async () => {
            const url = this.getApiUrl('files');
            const formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('purpose', file.type === 'application/pdf' ? 'assistants' : 'vision');
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${this.openAiKey}`,
                        // Content-Type is automatically set by FormData
                    },
                    body: formData,
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`OpenAI file upload failed: ${response.status} ${errorText}`);
                }
                const data = await response.json();
                if (!data.id) {
                    throw new Error('No file_id received from OpenAI');
                }
                return data.id;
            }
            catch (error) {
                throw error;
            }
        });
    }
    /**
     * Dedicated wrapper for moderation API calls with instrumentation
     */
    async createModeration(input, model = 'omni-moderation-latest') {
        return this.executeWithRetry(async (client) => {
            return client.moderations.create({
                input,
                model,
            });
        }, 5, 1000, 'moderation');
    }
    /**
     * Classify a prompt with a cheap non-streaming nano call.
     * Returns the text extracted from the first message-type output item.
     * Used by the LLM routing judge to pick the target model.
     */
    async classifyPrompt(text, systemPrompt, abortSignal) {
        const requestOptions = abortSignal ? { signal: abortSignal } : undefined;
        const response = await this.executeWithRetry(async (client) => client.responses.create({
            model: 'gpt-5.4-nano',
            input: text,
            instructions: systemPrompt,
            max_output_tokens: 30,
            stream: false,
        }, requestOptions), 1, 0, 'llm_routing_classify');
        if (response?.output_text != null) {
            return response.output_text;
        }
        const messageItem = response?.output?.find((item) => item.type === 'message');
        return (messageItem?.content
            ?.filter((c) => c.type === 'output_text')
            ?.map((c) => c.text)
            ?.join('') ?? '');
    }
    /**
     * Dedicated wrapper for audio transcription with instrumentation
     */
    async transcribeAudio(audioBlob) {
        // Call the existing method with instrumentation wrapper
        return timeOperation('audio_transcription', () => this.transcribeAudioWithModel(audioBlob), { size: audioBlob.size, type: audioBlob.type });
    }
    /**
     * Dedicated wrapper for speech synthesis with instrumentation
     */
    async synthesizeSpeech(text, voice, language) {
        // Call the existing method with instrumentation wrapper
        return timeOperation('tts_synthesis', () => this.synthesizeSpeechWithTTS(text, voice, language), { textLength: text.length, voice, language });
    }
    /**
     * Dedicated wrapper for PDF vision processing with instrumentation
     */
    async processPdfWithVision(file, prompt, model = 'gpt-4.1-mini') {
        // Call the existing method with instrumentation wrapper
        return timeOperation('pdf_vision', () => this.sendPdfToVisionApi(file, prompt, model), { fileSize: file.size, fileName: file.name, model });
    }
    /**
     * Dedicated wrapper for file upload with instrumentation
     */
    async uploadFile(file) {
        // Call the existing method with instrumentation wrapper
        return timeOperation('file_upload', () => this.uploadFileForAssistant(file), { fileSize: file.size, fileName: file.name, fileType: file.type });
    }
}
export default OpenAiWrapper;
